/**
 * Generic form utilities for react-hook-form and entity manipulation
 */

/**
 * Extract only dirty (changed) fields from form data
 * Useful for PATCH requests that should only send modified fields
 *
 * @template T - The form data type
 * @param data - The complete form data
 * @param dirtyFields - The dirtyFields object from react-hook-form
 * @returns Partial object containing only the fields that were modified
 *
 * @example
 * const data = { name: 'John', email: 'john@example.com', age: 30 };
 * const dirtyFields = { name: true, age: true };
 * const result = extractDirtyFields(data, dirtyFields);
 * // result = { name: 'John', age: 30 }
 */
export function extractDirtyFields<T extends Record<string, unknown>>(
  data: T,
  dirtyFields: Partial<Record<keyof T, boolean | object>>
): Partial<T> {
  const changedData: Partial<T> = {};

  // Iterate over dirtyFields and copy corresponding values from data
  (Object.keys(dirtyFields) as Array<keyof T>).forEach((key) => {
    if (dirtyFields[key]) {
      changedData[key] = data[key];
    }
  });

  return changedData;
}

/**
 * Extract _id from entity or return the value if it's already a string
 * Handles populated entities from the backend where relationships might be objects
 *
 * @param entity - Either an entity object with _id or a string ID
 * @returns The ID string or undefined
 *
 * @example
 * extractEntityId({ _id: '123', name: 'John' }) // '123'
 * extractEntityId('123') // '123'
 * extractEntityId(undefined) // undefined
 */
export function extractEntityId<T extends { _id: string }>(
  entity: T | string | undefined
): string | undefined {
  if (!entity) return undefined;
  if (typeof entity === 'string') return entity;
  return entity._id;
}

/**
 * Extract _id from multiple entities
 * Handles arrays of populated entities from the backend
 *
 * @param entities - Array of entity objects or string IDs
 * @returns Array of ID strings
 *
 * @example
 * extractEntityIds([{ _id: '1', name: 'A' }, { _id: '2', name: 'B' }]) // ['1', '2']
 * extractEntityIds(['1', '2']) // ['1', '2']
 * extractEntityIds([]) // []
 */
export function extractEntityIds<T extends { _id: string }>(
  entities: (T | string)[] | undefined
): string[] {
  if (!entities) return [];
  return entities.map((entity) => (typeof entity === 'string' ? entity : entity._id));
}

/**
 * Prepare form data by extracting IDs from populated entity fields
 * Useful when initializing forms with data from the backend
 *
 * @param entity - The entity with potentially populated relationships
 * @param fieldKeys - Array of field keys that should be converted to IDs
 * @returns New object with entity IDs extracted
 *
 * @example
 * const user = {
 *   name: 'John',
 *   manager: { _id: '123', name: 'Boss' },
 *   team: { _id: '456', name: 'Engineering' }
 * };
 * prepareFormData(user, ['manager', 'team']);
 * // { name: 'John', manager: '123', team: '456' }
 */
export function prepareFormData<T extends Record<string, unknown>>(
  entity: T,
  fieldKeys: (keyof T)[]
): T {
  const prepared = { ...entity };

  fieldKeys.forEach((key) => {
    if (prepared[key]) {
      const extracted = extractEntityId(prepared[key] as { _id: string } | string | undefined);
      prepared[key] = extracted as T[keyof T];
    }
  });

  return prepared;
}
